import { useQuery, useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query'
import { useCookie, useError } from 'react-use'

import { clients, utils } from '@digital-commerce/shared-utils'
import {
  getIdsToRemoveFromCart,
  hasFIFAProvisioner,
  getCartCouponCodes,
  getValidationErrors
} from '../utils/cart'
import createDigitalCommerceCartClient from '../clients/bff'

const client = createDigitalCommerceCartClient(clients.bff)

const {
  cart: { isHSCart, isCartMobilityAndHs }
} = utils

const PROMO_CODE_COOKIE = 'commercePromoCodeLog'

const useCart = () => {
  const queryClient = useQueryClient()
  const dispatchError = useError()
  const {
    data: cart,
    isLoading: isLoadingCart,
    isError,
    error: getCartError
  } = useQuery(['/cart'], client.getCart, {
    retry: (failureCount, error) => {
      // Don't retry if error response is 403/invalid auth token
      if (error.status === 403) {
        return false
      }
      // All other scenarios, allow one retry
      return failureCount === 0
    },
    onError: (error) => dispatchError(error)
  })

  const shouldValidateFifaCart = !!(cart && hasFIFAProvisioner(cart))
  const cartContainsInvalidProducts = !!(cart && (getValidationErrors(cart) || []).length > 0)
  const shouldFetchResolvingOffersErrorBanner =
    cartContainsInvalidProducts && (isHSCart(cart) || isCartMobilityAndHs(cart))

  // Conditional logic to call cart validatation when cart is loaded and contains FIFA products
  const {
    data: fifaValidateInfo,
    isFetching: isValidatingFifaCart,
    isError: hasFifaValidationFailure
  } = useQuery(['/cart/validate'], client.validateCart, {
    enabled: shouldValidateFifaCart,
    retry: 1,
    onError: (error) => dispatchError(error),
    onSuccess: (newData) => {
      queryClient.setQueryData(['/cart'], (oldData) => ({
        ...oldData,
        root: {
          ...oldData.root,
          eligibleFulfillmentMethods: [...newData.eligibleFulfillmentMethods],
          validationErrors: [...newData.validationErrors]
        }
      }))
    }
  })

  // Conditional logic to get error message banners (Resolving offers for HS products)
  const { data: resolvingOfferMessage, isFetching: isFetchingResolvingOffer } = useQuery(
    ['/cart/resolving-offer'],
    () => client.getResolvingOffer(cart.root),
    { enabled: shouldFetchResolvingOffersErrorBanner, retry: 0 }
  )

  // Remove cart items
  const isRemovingCartItems = useIsMutating(['/cart/remove-items'])
  const getCartItemsRemoveError = () => queryClient.getQueryData(['/cart/remove-items/error'])
  const setCartItemsRemoveError = (error) => {
    if (error.status === 403) dispatchError(error)
    queryClient.setQueryData(['/cart/remove-items/error'], error)
  }
  const { mutate: removeItems } = useMutation({
    mutationKey: ['/cart/remove-items'],
    mutationFn: async ({ items, postRemoval }) => {
      const itemIds = getIdsToRemoveFromCart(items)
      await client.removeItemsInCart({
        items: itemIds
      })
      if (typeof postRemoval === 'function') {
        postRemoval()
      }
      return queryClient.invalidateQueries({ queryKey: ['/cart'] })
    },
    onError: setCartItemsRemoveError
  })

  // Reset cart
  const isResettingCart = useIsMutating(['/cart/reset'])
  const { mutate: resetCart } = useMutation({
    mutationKey: ['/cart/reset'],
    mutationFn: async ({ language }) => {
      await client.resetCart({ language })
      return queryClient.invalidateQueries({ queryKey: ['/cart'] })
    },
    onError: setCartItemsRemoveError // use same onError as removing cart items
  })

  /* Start of coupon code */
  const [codeInCookie, , deleteCodeInCookie] = useCookie(PROMO_CODE_COOKIE)
  // Add coupon code
  const isRedeemingCode = useIsMutating(['/cart/redeem-code'])
  const getRedeemCodeError = () => queryClient.getQueryData(['/cart/redeem-code/error'])
  const setRedeemCodeError = (error) => queryClient.setQueryData(['/cart/redeem-code/error'], error)
  const getRedeemedCode = () => {
    // check queryClient in memory first.
    const code = queryClient.getQueryData(['/cart/redeem-code/code'])
    if (code) {
      return code
    }
    // If not found in query client, then this likely to be new page load, check cookie
    if (codeInCookie) {
      const codeInCookieWithoutValidationCode = codeInCookie.split('.')[0]
      const codesInCart = getCartCouponCodes(cart)
      if (codesInCart.includes(codeInCookieWithoutValidationCode)) {
        return codeInCookieWithoutValidationCode
      }
    }
    return undefined
  }
  const getCachedRedeemedCode = () => queryClient.getQueryData(['/cart/redeem-code/code']) // ignores cookie
  const setRedeemedCode = (code) => queryClient.setQueryData(['/cart/redeem-code/code'], code)

  const { mutate: redeemCode } = useMutation({
    mutationKey: ['/cart/redeem-code'],
    mutationFn: async (code) => {
      queryClient.removeQueries(['/cart/remove-code/success'])
      await client.redeemCode(code)
      queryClient.removeQueries({ queryKey: ['/cart/redeem-code/error'] })
      return queryClient.invalidateQueries({ queryKey: ['/cart'] })
    },
    onError: (error) => {
      if (error.status === 403) {
        dispatchError(error)
      } else {
        setRedeemCodeError(error)
      }
    },
    onSuccess: (data, variables) => setRedeemedCode(variables)
  })

  // Remove coupon code
  const isRemovingCode = useIsMutating(['/cart/remove-code'])
  const getRemoveCodeError = () => queryClient.getQueryData(['/cart/remove-code/error'])
  const setRemoveCodeError = (error) => queryClient.setQueryData(['/cart/remove-code/error'], error)
  const setRemoveCodeSuccess = (code) =>
    queryClient.setQueryData(['/cart/remove-code/success'], code)
  const getRemoveCodeSucess = () => queryClient.getQueryData(['/cart/remove-code/success'])
  const { mutate: removeCode } = useMutation({
    mutationKey: ['/cart/remove-code'],
    mutationFn: async (code) => {
      await client.removeCode(code)
      queryClient.invalidateQueries({ queryKey: ['/cart'] })
      setRemoveCodeSuccess(getRedeemedCode())
      queryClient.removeQueries({ queryKey: ['/cart/remove-code/error'] })
      setRedeemedCode(null)
      deleteCodeInCookie()
    },
    onError: (error) => {
      if (error.status === 403) {
        dispatchError(error)
      } else {
        setRemoveCodeError(error)
      }
    }
  })

  /* End of coupon code */

  // Update item fulfillment
  const isUpdatingItemFulfillment = useIsMutating(['/cart/update-item-fulfillment'])
  const getUpdatingItemFulfillmentError = () =>
    queryClient.getQueryData(['/cart/update-item-fulfillment/error'])
  const setUpdatingItemFulfillmentError = (error) =>
    queryClient.setQueryData(['/cart/update-item-fulfillment/error'], error)
  const { mutate: updateItemFulfillment } = useMutation({
    mutationKey: ['/cart/update-item-fulfillment'],
    mutationFn: async ({ itemId, fulfillment }) => {
      await client.updateItemFulfillment({ itemId, fulfillment })
      queryClient.invalidateQueries({ queryKey: ['/cart'] })
    },
    onError: setUpdatingItemFulfillmentError
  })

  // Log to BFF
  const { mutate: log } = useMutation({
    mutationKey: ['/cart/log'],
    mutationFn: async (payload) => {
      await client.log(payload)
    }
  })

  return {
    // Get cart
    cart,
    isLoadingCart,
    isError,
    getCartError,
    // Resolve offer
    isFetchingResolvingOffer,
    resolvingOfferMessage,
    // Validate cart
    hasFifaValidationFailure,
    isValidatingFifaCart,
    fifaValidateInfo,
    // Remove cart items
    isRemovingCartItems,
    removeItems,
    getCartItemsRemoveError,
    // Reset cart
    isResettingCart,
    resetCart,
    // Redeem code
    redeemCode,
    isRedeemingCode,
    getRedeemCodeError,
    getRedeemedCode,
    getCachedRedeemedCode,
    setRedeemedCode,
    // Remove code
    removeCode,
    isRemovingCode,
    getRemoveCodeError,
    getRemoveCodeSucess,
    // Update item fulfillment
    updateItemFulfillment,
    isUpdatingItemFulfillment,
    getUpdatingItemFulfillmentError,
    // Log to BFF
    log
  }
}

export default useCart
